const defaultNoticePeriods = [
  { minimumNoticeMinutes: 1440, feePercent: 0 },
  { minimumNoticeMinutes: 720, feePercent: 50 },
  { minimumNoticeMinutes: 0, feePercent: 100 },
];

const normalizeNoticePeriods = (periods) => {
  const source = Array.isArray(periods) && periods.length ? periods : defaultNoticePeriods;
  return source
    .map((period) => ({
      minimumNoticeMinutes: Number(period?.minimumNoticeMinutes ?? 0),
      feePercent: Number(period?.feePercent ?? 0),
    }))
    .sort((left, right) => right.minimumNoticeMinutes - left.minimumNoticeMinutes);
};

const isGlobalRule = (rule = {}) => (
  !rule.treatmentProductId
  && !rule.packageId
  && rule.memberOnly !== true
  && !rule.customerType
);

const audienceOf = (rule = {}) => {
  if (rule.memberOnly) return "MEMBER";
  return rule.customerType || "ALL";
};

const ruleToDraft = (rule = {}) => ({
  treatmentProductId: rule.treatmentProductId ? String(rule.treatmentProductId) : "",
  packageId: rule.packageId ? String(rule.packageId) : "",
  audience: audienceOf(rule),
  noticePeriods: normalizeNoticePeriods(rule.noticePeriods),
  noShowFeePercent: Number(rule.noShowFeePercent ?? 100),
});

export const defaultCancellationPolicyDraft = {
  globalRule: ruleToDraft(),
  targetedRules: [],
};

export const createCancellationPolicyRuleDraft = () => ({
  treatmentProductId: "",
  packageId: "",
  audience: "MEMBER",
  noticePeriods: normalizeNoticePeriods(),
  noShowFeePercent: 100,
});

export const cancellationPolicyToDraft = (policy = {}) => {
  const rules = Array.isArray(policy.rules) ? policy.rules : [];
  const globalRule = rules.find(isGlobalRule);
  return {
    globalRule: ruleToDraft(globalRule),
    targetedRules: rules.filter((rule) => !isGlobalRule(rule)).map(ruleToDraft),
  };
};

const numberInRange = (value, minimum, maximum) => {
  const number = Number(value);
  return Number.isFinite(number) && number >= minimum && number <= maximum;
};

const scopeKey = (rule) => [
  rule.treatmentProductId || "",
  rule.packageId || "",
  rule.audience || "ALL",
].join(":");

export const validateCancellationPolicyDraft = (draft = defaultCancellationPolicyDraft) => {
  const rules = [draft.globalRule, ...(draft.targetedRules || [])];
  const targetedRules = draft.targetedRules || [];

  if (targetedRules.some((rule) => !rule.treatmentProductId && !rule.packageId && rule.audience === "ALL")) {
    return "scopeRequired";
  }
  if (new Set(targetedRules.map(scopeKey)).size !== targetedRules.length) return "duplicateScope";

  for (const rule of rules) {
    const periods = Array.isArray(rule?.noticePeriods) ? rule.noticePeriods : [];
    if (!periods.length || periods.length > 20) return "intervalRequired";
    const thresholds = periods.map((period) => Number(period.minimumNoticeMinutes));
    if (new Set(thresholds).size !== thresholds.length) return "duplicateInterval";
    if (!thresholds.includes(0)) return "zeroIntervalRequired";
    if (periods.some((period) => (
      !Number.isInteger(Number(period.minimumNoticeMinutes))
      || !numberInRange(period.minimumNoticeMinutes, 0, 525600)
      || !numberInRange(period.feePercent, 0, 100)
    ))) return "invalidValue";
    if (!numberInRange(rule.noShowFeePercent, 0, 100)) return "invalidValue";
  }
  return "";
};

const draftRuleToPayload = (rule, global = false) => {
  const payload = {
    noticePeriods: normalizeNoticePeriods(rule.noticePeriods),
    noShowFeePercent: Number(rule.noShowFeePercent),
  };
  if (global) return payload;
  if (rule.treatmentProductId) payload.treatmentProductId = Number(rule.treatmentProductId);
  if (rule.packageId) payload.packageId = Number(rule.packageId);
  if (rule.audience === "MEMBER") payload.memberOnly = true;
  if (["HOTEL_GUEST", "EXTERNAL_GUEST"].includes(rule.audience)) payload.customerType = rule.audience;
  return payload;
};

export const buildCancellationPolicyPayload = (draft = defaultCancellationPolicyDraft) => ({
  rules: [
    draftRuleToPayload(draft.globalRule, true),
    ...(draft.targetedRules || []).map((rule) => draftRuleToPayload(rule)),
  ],
});

